import * as T from './Player.types';
import { COMPONENT_NAME } from './constants';
import { IRootState } from '../../reducer.types';
import { IPlayerState } from './reducer.types';

export const getPlayerState = (state: IRootState): IPlayerState => {
  return state[COMPONENT_NAME];
};

/*******************************************
 *                  Show
 ******************************************/
export const getShowId = (state: IRootState): null | string => {
  return getPlayerState(state).showId;
};

export const getShow = (state: IRootState): null | T.IShow => {
  return getPlayerState(state).showActionPayload;
};

export const getIsShowApiRequestPending = (state: IRootState): boolean => {
  return getPlayerState(state).isShowApiRequestPending;
};

export const getShowHeadline = (state: IRootState): string => {
  const show = getShow(state);

  if (show && show.headline) {
    return show.headline;
  }

  return '';
};

export const getShowSubtitle = (state: IRootState): string => {
  const show = getShow(state);

  if (show && show.subtitle) {
    return show.subtitle;
  }

  return '';
};

export const getShowImageUrl = (state: IRootState): string => {
  const show = getShow(state);

  if (show && show.image_url) {
    return show.image_url;
  }

  return '';
};

export const getIsShowApiRequestErrored = (state: IRootState): boolean => {
  return Boolean(getPlayerState(state).showApiRequestErrorMessage);
};

export const getShowApiRequestErrorMessage = (state: IRootState): string => {
  return getPlayerState(state).showApiRequestErrorMessage;
};

/*******************************************
 *                  Tracks
 ******************************************/
export const getTrack = (state: IRootState, trackId: string): null | T.ITrack => {
  const tracksById = getPlayerState(state).tracksById;

  if (tracksById) {
    return tracksById[trackId] || null;
  }

  return null;
};

export const getTracksById = (state: IRootState): null | T.ITracksById => {
  return getPlayerState(state).tracksById;
};

export const getNextAvailableTrackIdInPlaylist = (state: any): null | string => {
  const currentlyPlayingTrackId = getCurrentlyPlayingTrackId(state);
  const playlistIds = getPlaylistIds(state);

  if (Array.isArray(playlistIds) && playlistIds.length) {
    const currentlyPlayingTrackIdPosition = currentlyPlayingTrackId
      ? playlistIds.indexOf(currentlyPlayingTrackId)
      : -1;

    return playlistIds[currentlyPlayingTrackIdPosition + 1] || null;
  }
  return null;
};

export const getPrevAvailableTrackIdInPlaylist = (state: IRootState) => {
  const currentlyPlayingTrackId = getCurrentlyPlayingTrackId(state);
  const playlistIds = getPlaylistIds(state);

  if (currentlyPlayingTrackId && Array.isArray(playlistIds) && playlistIds.length) {
    const currentlyPlayingTrackIdPosition = playlistIds.indexOf(currentlyPlayingTrackId);

    if (currentlyPlayingTrackIdPosition > -1) {
      return playlistIds[currentlyPlayingTrackIdPosition - 1] || null;
    }
  }

  return null;
};

export const getFirstAvailableTrackIdInPlaylist = (state: IRootState): null | string => {
  const playlistIds = getPlaylistIds(state);

  if (Array.isArray(playlistIds) && playlistIds.length) {
    return playlistIds[0];
  }

  return null;
};

export const getLastAvailableTrackIdInPlaylist = (state: IRootState): null | string => {
  const playlistIds = getPlaylistIds(state);

  if (Array.isArray(playlistIds) && playlistIds.length) {
    return playlistIds[playlistIds.length - 1];
  }

  return null;
};

/*******************************************
 *                  Player
 ******************************************/
export const getPlayStatus = (state: IRootState): T.IPlayStatus => {
  return getPlayerState(state).playStatus;
};

export const getPlaylistIds = (state: IRootState): T.IPlaylistIds => {
  return getPlayerState(state).tracksIdsOrdered;
};

export const getCurrentlyPlayingTrackId = (state: IRootState): null | string => {
  return getPlayerState(state).currentlyPlayingTrackId || null;
};

export const getCurrentlyPlayingTrackUrl = (state: IRootState): null | string => {
  const currentlyPlayingTrackId = getCurrentlyPlayingTrackId(state);

  if (currentlyPlayingTrackId) {
    const currentlyPlayingTrack = getTrack(state, currentlyPlayingTrackId);

    if (currentlyPlayingTrack) {
      return currentlyPlayingTrack.track.url;
    }
  }

  return null;
};

export const getCurrentlyPlayingTrackHeadline = (state: IRootState) => {
  const currentlyPlayingTrackId = getCurrentlyPlayingTrackId(state);

  if (currentlyPlayingTrackId) {
    const currentlyPlayingTrack = getTrack(state, currentlyPlayingTrackId);

    if (currentlyPlayingTrack) {
      return currentlyPlayingTrack.headline;
    }
  }

  return '';
};

export const getDurationPlayedInPermille = (state: IRootState): number => {
  return getPlayerState(state).durationPlayedInPermille;
};

export const getIsSeeking = (state: IRootState): boolean => {
  return getPlayerState(state).isSeeking;
};

export const getCurrentlyPlayingTrackDuration = (state: IRootState): number => {
  return getPlayerState(state).currentlyPlayingTrackDuration;
};

export const getDurationPlayedInSeconds = (state: IRootState): number => {
  return getPlayerState(state).durationPlayedInSeconds;
};

export const getIsTrackApiRequestPending = (state: IRootState): boolean => {
  return getPlayerState(state).isTrackApiRequestPending;
};
